import React, { useState, useEffect, useContext, useMemo } from 'react';
import Chart from '../../../../Assets/Images/gap-analysis-chart.png';
import Likert from '../../../Charts/Likert/Likert';
import HeaderPreview from '../../../GeneratePdf/HeaderPreview';
import FooterPreview from '../../../GeneratePdf/FooterPreview';
import GroupReportContext from '../../../../Context/GroupReportContext';
import GroupQuardantsFlow from './GroupQuardantsFlow';
import { applyFullTieOrder } from '../../../../utils/quadrantTieOrder';

// Hierarchy for tie-breaking: lower number = higher priority
const LEADERSHIP_HIERARCHY = {
  collaborative: 0,  // Highest priority
  visionary: 1,
  strategic: 2,
  performance: 3    // Lowest priority
};

// Comparator function for sorting leadership quadrants with tie-breaking
const compareLeadershipQuadrants = (a, b) => {
  // First compare by score (descending)
  const scoreDiff = b.score - a.score;
  if (scoreDiff !== 0) return scoreDiff;
  
  // If scores are equal, compare by hierarchy (ascending - lower number = higher priority)
  const aHierarchy = LEADERSHIP_HIERARCHY[a.name.toLowerCase()] ?? 999;
  const bHierarchy = LEADERSHIP_HIERARCHY[b.name.toLowerCase()] ?? 999;
  return aHierarchy - bHierarchy;
};

// Comparator for objects with 'name' and 'value' properties
const compareLeadershipByValue = (a, b) => {
  // First compare by value (descending)
  const valueDiff = b.value - a.value;
  if (valueDiff !== 0) return valueDiff;
  
  // If values are equal, compare by hierarchy
  const aHierarchy = LEADERSHIP_HIERARCHY[a.name.toLowerCase()] ?? 999;
  const bHierarchy = LEADERSHIP_HIERARCHY[b.name.toLowerCase()] ?? 999;
  return aHierarchy - bHierarchy;
};

const GroupAlignmentFlowFirst = ({ apiData, previewData, reportType, pageNumber }) => {

  const { averagePercentages, averagePercentagesLeadership } = useContext(GroupReportContext)

  const [percentages, setPercentages] = useState({})
  const sortedScoresLabel = useMemo(() => {
    if (!averagePercentagesLeadership) return [];
    const labels = [
      { name: 'Strategic', value: averagePercentagesLeadership[0]?.avg ?? 0 },
      { name: 'Visionary', value: averagePercentagesLeadership[1]?.avg ?? 0 },
      { name: 'Collaborative', value: averagePercentagesLeadership[2]?.avg ?? 0 },
      { name: 'Performance', value: averagePercentagesLeadership[3]?.avg ?? 0 },
    ];
    return [...labels].sort(compareLeadershipByValue);
  }, [averagePercentagesLeadership]);

  const styles = {
    page: { padding: '16px 0px', backgroundColor: '#FFFFFF' },
    header: { display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #00577D', paddingBottom: 5, marginBottom: '2px', marginTop: '-15px', },
    titleMain: { fontSize: '12px', fontWeight: '700', color: '#76A3BE' },
    title: { fontSize: '11px', fontWeight: '200', color: '#76A3BE' },
    rightSection: { textAlign: 'right' },
    rightText: { fontSize: '8px', fontWeight: '700', color: '#76A3BE' },
    headerText: { fontSize: '11px', lineHeight: '15px', fontWeight: '700', color: '#00577D', marginBottom: 0, marginTop: 0, fontFamily: 'Helvetica', },
    headerTextb: { fontSize: '14px', lineHeight: '18px', fontWeight: '700', color: '#00577D', marginBottom: 0, marginTop: 0, fontFamily: 'Helvetica', },
    paragraph: { fontSize: '16px', fontWeight: '400', color: '#245775', lineHeight: '24px', marginTop: 0 },
    chart: { width: '100%', maxWidth: '510px', height: 'auto', display: 'block', margin: '20px auto' },
    footer: { display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #00577D', paddingTop: 10, marginTop: 20 },
    footerTextHeading: { fontSize: '12px', fontWeight: '700', color: '#76A3BE' },
    footerText: { fontSize: '10px', color: '#76A3BE' },
    headerSubtitle: { fontFamily: "Helvetica", fontWeight: 400, fontStyle: "normal", fontSize: "11px", lineHeight: "15px", letterSpacing: "0px", color: "#C99212", margin: "0px 0" , marginBottom : "16px" },
    containers: { textAlign: 'center' },
    chartHead: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '80px' },
    chartheading: { fontFamily: 'Helvetica', fontWeight: 700, fontStyle: 'normal', fontSize: '8px', lineHeight: '12px', letterSpacing: '0%', color: '#000000', verticalAlign: 'middle', leadingTrim: 'cap-height' },
    percentages: { display: 'flex', flexDirection: 'row', position: 'relative', paddingBottom: '22px', width: "86%", margin: "0px auto" },
    percentTwentyLeft: { textAlign: 'center', position: 'absolute', left: 'calc(40%)', fontSize: '8px', marginTop: "5px", color: "#000000" },
    percentFourtyLeft: { textAlign: 'center', position: 'absolute', left: 'calc(31.5%)', fontSize: '8px', marginTop: "5px", color: "#000000" },
    percentSixtyLeft: { textAlign: 'center', position: 'absolute', left: 'calc(23%)', fontSize: '8px', marginTop: "5px", color: "#000000" },
    percentEightyLeft: { textAlign: 'center', position: 'absolute', left: 'calc(14.5%)', fontSize: '8px', marginTop: "5px", color: "#000000" },
    percentHundreadLeft: { textAlign: 'center', position: 'absolute', left: 'calc(4%)', fontSize: '8px', marginTop: "5px", color: "#000000" },
    vsSection: { fontWeight: "700", color: "#00577D", fontSize: "11px", textAlign: "center", paddingTop:"16px", fontFamily: "Helvetica" },
    percentTwentyRight: { textAlign: 'center', position: 'absolute', right: 'calc(38.5%)', fontSize: '8px', marginTop: "5px", color: "#000000" },
    percentFourtyRight: { textAlign: 'center', position: 'absolute', right: 'calc(29.5%)', fontSize: '8px', marginTop: "5px", color: "#000000" },
    percentSixtyRight: { textAlign: 'center', position: 'absolute', right: 'calc(21.1%)', fontSize: '8px', marginTop: "5px", color: "#000000" },
    percentEightRight: { textAlign: 'center', position: 'absolute', right: 'calc(12.5%)', fontSize: '8px', marginTop: "5px", color: "#000000" },
    percentHundreadRight: { textAlign: 'center', position: 'absolute', right: 'calc(3%)', fontSize: '8px', marginTop: "5px", color: "#000000", },
  };

  useEffect(() => {
    const calculatedPercentages = {
      twentyLeft: 20, // Replace with dynamic logic
      fourtyLeft: 40, // Replace with dynamic logic
      sixtyLeft: 60, // Replace with dynamic logic
      eightyLeft: 80, // Replace with dynamic logic
      hundreadLeft: 100, // Replace with dynamic logic

      twentyRight: 20, // Replace with dynamic logic
      fourtyRight: 40, // Replace with dynamic logic
      sixtyRight: 60, // Replace with dynamic logic
      eightyRight: 80, // Replace with dynamic logic
      hundreadRight: 100, // Replace with dynamic logic
    };
    setPercentages(calculatedPercentages);
  }, [apiData]);



  const strength = averagePercentages?.find((el) => el?.name == "Strength")?.avg
  const heart = averagePercentages?.find((el) => el?.name == "Heart")?.avg
  const soul = averagePercentages?.find((el) => el?.name == "Soul")?.avg
  const mind = averagePercentages?.find((el) => el?.name == "Mind")?.avg

  const strategic = averagePercentagesLeadership?.find((el) => el?.name == "Strategic")?.avg
  const visionary = averagePercentagesLeadership?.find((el) => el?.name == "Visionary")?.avg
  const collaborative = averagePercentagesLeadership?.find((el) => el?.name == "Collaborative")?.avg
  const performance = averagePercentagesLeadership?.find((el) => el?.name == "Performance")?.avg

  const pairedResults = [
    {
      "label": "Heart",
      "pairedLabel": "Collaborative",
      "score1": heart,
      "score2": collaborative,
      "colors": [
        "#B6111F",
        "#DB888F"
      ]
    },
    {
      "label": "Soul",
      "pairedLabel": "Visionary",
      "score1": soul,
      "score2": visionary,
      "colors": [
        "#00577D",
        "#80ABBE"
      ]
    },
    {
      "label": "Mind",
      "pairedLabel": "Strategic",
      "score1": mind,
      "score2": strategic,
      "colors": [
        "#426B88",
        "#A0B5C3"
      ]
    },
    {
      "label": "Strength",
      "pairedLabel": "Performative",
      "score1": strength,
      "score2": performance,
      "colors": [
        "#C99212",
        "#E4C988"
      ]
    },
  ]

  const SquareWithArrow = ({ scores }) => {
    const topLeft = 'STRATEGIC';
    const topRight = 'PERFORMANCE';
    const bottomLeft = 'VISIONARY';
    const bottomRight = 'COLLABORATIVE';

    const topLeftValue = (scores?.find(s => s.name === 'strategic')?.value || 0).toFixed(2);
    const topRightValue = (scores?.find(s => s.name === 'performance')?.value || 0).toFixed(2);
    const bottomLeftValue = (scores?.find(s => s.name === 'visionary')?.value || 0).toFixed(2);
    const bottomRightValue = (scores?.find(s => s.name === 'collaborative')?.value || 0).toFixed(2);

    const labelStyle = { color: '#000', fontSize: '9px', fontWeight: 700, fontFamily: 'Helvetica, sans-serif', textTransform: 'uppercase', position: 'absolute', lineHeight: '13px' };
    const percentageStyle = { color: '#000', fontSize: '9px', fontWeight: 700, fontFamily: 'Helvetica, sans-serif', position: 'absolute', lineHeight: '13px' };

    const diagramSize = 206;
    const headLength = 17;
    const headWidth = 11;
    const centerOffsetX = diagramSize / 2;
    const centerOffsetY = diagramSize / 2;
    const quadrantOffsetX = centerOffsetX / 2;
    const quadrantOffsetY = centerOffsetY / 2;

    const quadrantCenters = {
      strategic: { x: quadrantOffsetX, y: quadrantOffsetY },
      performance: { x: diagramSize - quadrantOffsetX, y: quadrantOffsetY },
      visionary: { x: quadrantOffsetX, y: diagramSize - quadrantOffsetY },
      collaborative: { x: diagramSize - quadrantOffsetX, y: diagramSize - quadrantOffsetY }
    };

    const quadrantScores = [
      { name: 'strategic', score: scores?.find(s => s.name === 'strategic')?.value || 0 },
      { name: 'performance', score: scores?.find(s => s.name === 'performance')?.value || 0 },
      { name: 'visionary', score: scores?.find(s => s.name === 'visionary')?.value || 0 },
      { name: 'collaborative', score: scores?.find(s => s.name === 'collaborative')?.value || 0 }
    ];

    const sortedQuadrants = applyFullTieOrder([...quadrantScores].sort(compareLeadershipQuadrants), (q) => q.name, (q) => q.score)
      .map(({ name, score }) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value: score
      }));

    const pathPoints = sortedQuadrants.map(step => quadrantCenters[step.name.toLowerCase()]);

    let arrowPath = '';
    if (pathPoints.length > 0) {
      const pointsForPath = [...pathPoints];
      if (pointsForPath.length > 1) {
        const tip = pointsForPath[pointsForPath.length - 1];
        const prev = pointsForPath[pointsForPath.length - 2];
        const dx = tip.x - prev.x;
        const dy = tip.y - prev.y;
        const len = Math.hypot(dx, dy) || 1;
        const trimmedTip = {
          x: tip.x - (headLength * dx) / len,
          y: tip.y - (headLength * dy) / len
        };
        pointsForPath[pointsForPath.length - 1] = trimmedTip;
      }

      arrowPath = `M ${pointsForPath[0].x} ${pointsForPath[0].y}`;
      for (let i = 1; i < pointsForPath.length; i++) {
        arrowPath += ` L ${pointsForPath[i].x} ${pointsForPath[i].y}`;
      }
    }

    const dotPosition = sortedQuadrants[0]
      ? quadrantCenters[sortedQuadrants[0].name.toLowerCase()]
      : { x: 0, y: 0 };

    const arrowColor = '#B6111F';
    let arrowHeadPoints = '';

    if (sortedQuadrants.length > 1) {
      const lastIdx = sortedQuadrants.length - 1;
      const tip = quadrantCenters[sortedQuadrants[lastIdx].name.toLowerCase()];
      const prev = quadrantCenters[sortedQuadrants[lastIdx - 1].name.toLowerCase()];
      const angle = Math.atan2(tip.y - prev.y, tip.x - prev.x);
      const left = {
        x: tip.x - headLength * Math.cos(angle) + headWidth * Math.sin(angle),
        y: tip.y - headLength * Math.sin(angle) - headWidth * Math.cos(angle)
      };
      const right = {
        x: tip.x - headLength * Math.cos(angle) - headWidth * Math.sin(angle),
        y: tip.y - headLength * Math.sin(angle) + headWidth * Math.cos(angle)
      };
      arrowHeadPoints = `${tip.x},${tip.y} ${left.x},${left.y} ${right.x},${right.y}`;
    }

    return (
      <div style={{ position: 'relative', width: `${diagramSize}px`, height: `${diagramSize}px`, backgroundColor: 'transparent', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '2px solid #000', margin: "16px 0px" }}>

        <div style={{ ...labelStyle, top: '6px', left: '10px' }}>
          {topLeft}
        </div>
        <div style={{ ...labelStyle, top: '6px', right: '10px' }}>
          {topRight}
        </div>

        <div style={{ ...percentageStyle, top: '20px', left: '10px' }}>
          {topLeftValue}%
        </div>

        <div style={{ ...percentageStyle, top: '20px', right: '10px' }}>
          {topRightValue}%
        </div>

        <div style={{ ...percentageStyle, bottom: '20px', left: '10px' }}>
          {bottomLeftValue}%
        </div>
        <div style={{ ...labelStyle, bottom: '6px', left: '10px' }}>
          {bottomLeft}
        </div>

        <div style={{ ...percentageStyle, bottom: '20px', right: '10px' }}>
          {bottomRightValue}%
        </div>
        <div style={{ ...labelStyle, bottom: '6px', right: '10px' }}>
          {bottomRight}
        </div>

        <div style={{ position: 'absolute', width: '2px', height: '100%', backgroundColor: '#000' }}></div>
        <div style={{ position: 'absolute', width: '100%', height: '2px', backgroundColor: '#000' }}></div>

        <svg width={diagramSize} height={diagramSize} style={{ position: 'absolute', top: 0, left: 0 }}>
          <circle cx={dotPosition.x} cy={dotPosition.y} r="7.5" fill={arrowColor} />
          <path
            d={arrowPath}
            stroke={arrowColor}
            strokeWidth="4"
            strokeOpacity="1"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          {arrowHeadPoints && (
            <polygon points={arrowHeadPoints} fill={arrowColor} stroke={arrowColor} />
          )}
        </svg>
      </div>
    );
  };

  return (
    <div>
      <HeaderPreview />
      <div style={styles.page}>

        <div style={{}}>
          <h1 style={styles.headerTextb}>GAP ANALYSIS</h1>
          <p style={styles.headerSubtitle}>Gap Awareness, Alignment, & Integration</p>
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "24px", marginTop: "16px" }}>
          <div style={styles.containers}>
            <div style={styles.headerText}>GROUP LIFE FLOW</div>
            <GroupQuardantsFlow showFlowText={false} size={206} radius={"7.5"} />
          </div>

          <div style={styles.containers}>
            <div style={styles.headerText}>GROUP LEADERSHIP FLOW</div>
            <SquareWithArrow
              scores={sortedScoresLabel.map((item) => ({
                name: item.name.toLowerCase(),
                value: item.value
              }))}
            />
          </div>
        </div>

        <p style={styles.vsSection}>WHO YOU ARE vs HOW YOU LEAD</p>

        <div style={styles.chartHead}>
          <p style={styles.chartheading}>WHO YOU ARE</p>
          <p style={styles.chartheading}>HOW YOU LEAD</p>
        </div>

        {pairedResults.map(({ label, pairedLabel, score1, score2, colors }) => (
          <Likert
            key={label}
            left_labels={[{ label, color: colors[0] }]}
            right_labels={[{ label: pairedLabel, color: colors[1] }]}
            responses={{
              total: 100,
              data: {
                [label]: score1,
                [pairedLabel]: score2
              }
            }}
          />
        ))}
        <div style={styles.percentages}>
          <p style={styles.percentTwentyLeft}>{percentages.twentyLeft}%</p>
          <p style={styles.percentFourtyLeft}>{percentages.fourtyLeft}%</p>
          <p style={styles.percentSixtyLeft}>{percentages.sixtyLeft}%</p>
          <p style={styles.percentEightyLeft}>{percentages.eightyLeft}%</p>
          <p style={styles.percentHundreadLeft}>{percentages.hundreadLeft}%</p>

          <p style={styles.percentTwentyRight}>{percentages.twentyRight}%</p>
          <p style={styles.percentFourtyRight}>{percentages.fourtyRight}%</p>
          <p style={styles.percentSixtyRight}>{percentages.sixtyRight}%</p>
          <p style={styles.percentEightRight}>{percentages.eightyRight}%</p>
          <p style={styles.percentHundreadRight}>{percentages.hundreadRight}%</p>
        </div>
      </div>


      <FooterPreview pageNumber={pageNumber} reportType={reportType} />
    </div>
  );
};

export default GroupAlignmentFlowFirst;
